import { useState, type ReactNode } from "react";
import { cn } from "../lib/utils";

export type KanbanPriority = "low" | "medium" | "high" | "urgent";

export interface KanbanItem {
  id: string;
  title: string;
  description?: string;
  priority?: KanbanPriority;
  tags?: string[];
  assignee?: string;
  assigneeFallback?: string;
}

export interface KanbanColumn {
  id: string;
  title: string;
  color?: string;
  items: KanbanItem[];
}

interface KanbanProps {
  columns: KanbanColumn[];
  onChange?: (columns: KanbanColumn[]) => void;
  className?: string;
}

const priorityColors: Record<KanbanPriority, string> = {
  low: "#6b7280", medium: "#f59e0b", high: "#f97316", urgent: "#ef4444",
};

export function Kanban({ columns: initial, onChange, className }: KanbanProps) {
  const [cols, setCols] = useState(initial);
  const [dragging, setDragging] = useState<{ itemId: string; fromCol: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);

  function update(next: KanbanColumn[]) { setCols(next); onChange?.(next); }

  function onDragStart(itemId: string, fromCol: string) {
    setDragging({ itemId, fromCol });
  }
  function onDrop(toColId: string) {
    if (!dragging) return;
    const { itemId, fromCol } = dragging;
    if (fromCol === toColId) { setDragging(null); setOver(null); return; }
    const next = cols.map(c => {
      if (c.id === fromCol) return { ...c, items: c.items.filter(i => i.id !== itemId) };
      if (c.id === toColId) {
        const item = cols.find(c2 => c2.id === fromCol)!.items.find(i => i.id === itemId)!;
        return { ...c, items: [...c.items, item] };
      }
      return c;
    });
    update(next);
    setDragging(null);
    setOver(null);
  }

  return (
    <div className={cn("kanban", className)}>
      {cols.map(col => (
        <div
          key={col.id}
          className={cn("kanban-col", over === col.id && "kanban-col-over")}
          onDragOver={e => { e.preventDefault(); setOver(col.id); }}
          onDragLeave={() => setOver(null)}
          onDrop={() => onDrop(col.id)}
        >
          <div className="kanban-col-header">
            <div className="kanban-col-dot" style={{ background: col.color ?? "var(--primary)" }} />
            <span className="kanban-col-title">{col.title}</span>
            <span className="kanban-col-count">{col.items.length}</span>
          </div>
          <div className="kanban-items">
            {col.items.map(item => (
              <div
                key={item.id}
                className={cn("kanban-card", dragging?.itemId === item.id && "kanban-card-dragging")}
                draggable
                onDragStart={() => onDragStart(item.id, col.id)}
                onDragEnd={() => { setDragging(null); setOver(null); }}
              >
                {item.priority && (
                  <span className="kanban-priority" style={{ background: priorityColors[item.priority] }}>
                    {item.priority}
                  </span>
                )}
                <p className="kanban-card-title">{item.title}</p>
                {item.description && <p className="kanban-card-desc">{item.description}</p>}
                {item.tags && item.tags.length > 0 && (
                  <div className="kanban-tags">
                    {item.tags.map(t => <span key={t} className="kanban-tag">{t}</span>)}
                  </div>
                )}
                {item.assignee && (
                  <div className="kanban-assignee">
                    <span className="kanban-avatar">{item.assigneeFallback ?? item.assignee[0].toUpperCase()}</span>
                  </div>
                )}
              </div>
            ))}
            {col.items.length === 0 && (
              <div className="kanban-empty">Drop cards here</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
